import { useState } from "react";
import { X, Mail, Edit3 } from "lucide-react";
import { colors, fonts, CONTACT_TYPES } from "../styles/theme";
import { Badge, Button } from "./ui";
import { useTenant } from "../contexts/TenantContext";
import {
  updateContact,
  deriveContactType,
  displayName,
  initials,
  fmtDate,
} from "../services/api";
import EditContactForm from "../features/contact-drawer/EditContactForm";
import TypeSelector from "../features/contact-drawer/TypeSelector";
import EngagementScoreBadge from "../features/contact-drawer/EngagementScoreBadge";
import EmailFrequencySelector from "../features/contact-drawer/EmailFrequencySelector";
import TagsSection from "../features/contact-drawer/TagsSection";
import NotesSection from "../features/contact-drawer/NotesSection";
import ActivityTimeline from "../features/contact-drawer/ActivityTimeline";
import EmailHistory from "../features/contact-drawer/EmailHistory";
import NdaSection from "../features/contact-drawer/NdaSection";

export function ContactDrawer({ contact, onClose, onContactUpdate }) {
  const { features } = useTenant();
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  if (!contact) return null;

  const typeInfo =
    CONTACT_TYPES[deriveContactType(contact.tags, contact.ghlTags)] ||
    CONTACT_TYPES.subscriber;

  const startEdit = () => {
    setEditForm({
      firstName: contact.firstName || "",
      lastName: contact.lastName || "",
      email: contact.email || "",
      phone: contact.phone || "",
      company: contact.company || "",
      jobTitle: contact.customFields?.jobTitle || "",
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const updates = {
        email: contact.email,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        phone: editForm.phone,
        company: editForm.company,
        customFields: { ...(contact.customFields || {}), jobTitle: editForm.jobTitle },
      };
      const result = await updateContact(contact.id, updates);
      if (onContactUpdate) onContactUpdate(result);
      setEditing(false);
    } catch (err) {
      console.error("Failed to save contact:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 99,
        }}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "calc(100% - 260px)",
          background: colors.bgCard,
          borderLeft: `1px solid ${colors.border}`,
          zIndex: 100,
          padding: 32,
          overflowY: "auto",
          boxShadow: "-20px 0 60px rgba(0,0,0,0.12)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
          }}
        >
          <h2
            style={{
              fontFamily: fonts.heading,
              fontSize: 20,
              color: colors.text,
            }}
          >
            Contact Details
          </h2>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {!editing && (
              <Button variant="default" icon={Edit3} onClick={startEdit} style={{ fontSize: 12, padding: "4px 10px" }}>
                Edit
              </Button>
            )}
            <X
              size={20}
              color={colors.textSecondary}
              style={{ cursor: "pointer" }}
              onClick={onClose}
            />
          </div>
        </div>

        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: typeInfo.bg,
            color: typeInfo.color,
            fontFamily: fonts.heading,
            fontWeight: 700,
            fontSize: 22,
            marginBottom: 16,
          }}
        >
          {initials(contact)}
        </div>

        {editing ? (
          <EditContactForm
            form={editForm}
            onChange={setEditForm}
            onSave={saveEdit}
            onCancel={() => setEditing(false)}
            saving={saving}
          />
        ) : (
          <>
            <h3
              style={{
                fontFamily: fonts.heading,
                fontSize: 18,
                color: colors.text,
                marginBottom: 4,
              }}
            >
              {displayName(contact)}
            </h3>
            <p
              style={{
                fontSize: 13,
                color: colors.textSecondary,
                marginBottom: 4,
              }}
            >
              {contact.company}
            </p>
            {contact.customFields?.jobTitle && (
              <p
                style={{
                  fontSize: 12,
                  color: colors.textSecondary,
                  marginBottom: 16,
                }}
              >
                {contact.customFields.jobTitle}
              </p>
            )}

            <TypeSelector
              contact={contact}
              typeInfo={typeInfo}
              onContactUpdate={onContactUpdate}
            />

            <EngagementScoreBadge contact={contact} />

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                marginBottom: 24,
              }}
            >
              {[
                { label: "Email", value: contact.email },
                { label: "Phone", value: contact.phone || "—" },
                { label: "Source", value: contact.source || "crm" },
                {
                  label: "Created",
                  value: contact.createdAt ? fmtDate(contact.createdAt) : "—",
                },
              ].map((field) => (
                <div key={field.label}>
                  <div
                    style={{
                      fontSize: 11,
                      color: colors.textSecondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: 3,
                    }}
                  >
                    {field.label}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: colors.text,
                      fontFamily:
                        field.label === "Email" ? fonts.mono : fonts.body,
                    }}
                  >
                    {field.value}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <TagsSection contact={contact} onContactUpdate={onContactUpdate} />

        <EmailFrequencySelector contact={contact} />

        <NotesSection contactId={contact.id} />

        <ActivityTimeline contactId={contact.id} />

        {features?.o365 && (
          <EmailHistory contactId={contact.id} contactEmail={contact.email} />
        )}

        <NdaSection contact={contact} />

        <div style={{ display: "flex", gap: 8, marginTop: 24 }}>
          <Button variant="primary" icon={Mail} style={{ flex: 1 }}>
            Send Email
          </Button>
        </div>
      </div>
    </>
  );
}
